// ============================================
// OFFLINE HELPER - NETWORK & CACHE MANAGEMENT
// ============================================

// Cache-Keys
const CACHE_PREFIX = 'restroom_advisor_';
const CACHE_VERSION = 'v1_';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 Stunden

// ============================================
// NETWORK STATUS
// ============================================

/**
 * Prüft ob Browser online ist
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Event-Listener für Online/Offline
 */
export const setupNetworkListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  // Cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// ============================================
// CACHE MANAGEMENT
// ============================================

/**
 * Speichert Daten im LocalStorage mit Timestamp
 */
export const cacheData = (key, data) => {
  try {
    const cacheEntry = {
      data: data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    return true;
  } catch (error) {
    console.error('❌ Cache-Fehler (speichern):', error);
    // LocalStorage voll? -> Lösche älteste Einträge
    if (error.name === 'QuotaExceededError') {
      clearOldestCache();
      // Versuche nochmal
      try {
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
          data: data,
          timestamp: Date.now(),
          version: CACHE_VERSION
        }));
        return true;
      } catch (retryError) {
        console.error('❌ Cache erneut voll:', retryError);
        return false;
      }
    }
    return false;
  }
};

/**
 * Liest Daten aus Cache
 */
export const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + key);
    if (!cached) return null;
    
    const cacheEntry = JSON.parse(cached);
    
    // Version-Check
    if (cacheEntry.version !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    
    // Expiry-Check
    const age = Date.now() - cacheEntry.timestamp;
    if (age > CACHE_EXPIRY) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    
    return cacheEntry.data;
  } catch (error) {
    console.error('❌ Cache-Fehler (lesen):', error);
    return null;
  }
};

/**
 * Löscht spezifischen Cache-Eintrag
 */
export const clearCache = (key) => {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
    return true;
  } catch (error) {
    console.error('❌ Cache-Fehler (löschen):', error);
    return false;
  }
};

/**
 * Löscht ALLE App-Caches
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage);
    let count = 0;
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
        count++;
      }
    });
    return count; // Gibt die Anzahl der gelöschten Einträge zurück
  } catch (error) {
    console.error('❌ Fehler beim Löschen aller Caches:', error);
    return 0;
  }
};

/**
 * Löscht älteste Cache-Einträge (bei vollem Storage)
 */
const clearOldestCache = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(CACHE_PREFIX));
    
    // Sortiere nach Timestamp
    const entries = cacheKeys.map(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        return { key, timestamp: data.timestamp || 0 };
      } catch {
        return { key, timestamp: 0 };
      }
    }).sort((a, b) => a.timestamp - b.timestamp);
    
    // Lösche älteste 25%
    const toDelete = Math.ceil(entries.length * 0.25) || 1;
    
    for (let i = 0; i < toDelete; i++) {
      localStorage.removeItem(entries[i].key);
    }
  } catch (error) {
    console.error('❌ Fehler beim Löschen alter Caches:', error);
  }
};

/**
 * Gibt Cache-Statistiken zurück
 */
export const getCacheStats = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(CACHE_PREFIX));
    
    let totalSize = 0;
    const entries = [];
    
    cacheKeys.forEach(key => {
      const value = localStorage.getItem(key);
      const size = new Blob([value]).size;
      totalSize += size;
      
      try {
        const data = JSON.parse(value);
        entries.push({
          key: key.replace(CACHE_PREFIX, ''),
          size: size,
          age: Date.now() - (data.timestamp || 0),
          version: data.version
        });
      } catch {}
    });
    
    return {
      count: cacheKeys.length,
      totalSize: totalSize,
      entries: entries.sort((a, b) => b.size - a.size)
    };
  } catch (error) {
    console.error('❌ Cache-Stats-Fehler:', error);
    return { count: 0, totalSize: 0, entries: [] };
  }
};

// ============================================
// STÄDTE-SPEZIFISCHE CACHE-FUNKTIONEN
// ============================================
export const cacheToiletsByCity = (city, toilets) => {
  return cacheData(`city_${city}`, toilets);
};

/**
 * Lädt Toiletten einer Stadt aus Cache
 */
export const getCachedToiletsByCity = (city) => {
  return getCachedData(`city_${city}`);
};

/**
 * Speichert User-Daten im Cache
 */
export const cacheUserData = (userId, userData) => {
  return cacheData(`user_${userId}`, userData);
};

/**
 * Lädt User-Daten aus Cache
 */
export const getCachedUserData = (userId) => {
  return getCachedData(`user_${userId}`);
};

// ============================================
// OFFLINE-QUEUE (für späteres Sync)
// ============================================

const QUEUE_KEY = CACHE_PREFIX + 'offline_queue';

/**
 * Fügt Aktion zur Offline-Queue hinzu
 */
export const addToOfflineQueue = (action) => {
  try {
    const queue = getOfflineQueue();
    queue.push({
      ...action,
      timestamp: Date.now(),
      id: Date.now() + Math.random()
    });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch (error) {
    console.error('❌ Queue-Fehler:', error);
    return false;
  }
};

/**
 * Gibt Offline-Queue zurück
 */
export const getOfflineQueue = () => {
  try {
    const queue = localStorage.getItem(QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    console.error('❌ Queue-Fehler:', error);
    return [];
  }
};

/**
 * Löscht Offline-Queue
 */
export const clearOfflineQueue = () => {
  try {
    localStorage.removeItem(QUEUE_KEY);
    return true;
  } catch (error) {
    console.error('❌ Queue-Fehler:', error);
    return false;
  }
};

/**
 * Entfernt Aktion aus Queue
 */
export const removeFromOfflineQueue = (actionId) => {
  try {
    const queue = getOfflineQueue();
    const filtered = queue.filter(a => a.id !== actionId);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('❌ Queue-Fehler:', error);
    return false;
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Formatiert Bytes zu lesbarer Größe
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Formatiert Zeitdauer
 */
export const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};
